import { Role, SessionActivity } from '@shared/entities';
import {
  holidayDates,
  SessionDayType,
  TItemDefaultField,
  TItemDefaults,
  WorkTimeType,
} from '@shared/types';
import dayjs, { Dayjs } from 'dayjs';
import { getIsExtra } from './session-helpers';

const MS_PER_HOUR = 1000 * 60 * 60;

/**
 * Work Time Types by Hours
 */
export const getIsStraightTime = (hours: number) => hours > 0 && hours <= 8;
export const getIsTimeAndOneHalf = (hours: number) => hours > 8 && hours <= 10;
export const getIsDoubleTime = (hours: number) => hours > 10;
export const getIsOvertime = (hours: number) =>
  getIsTimeAndOneHalf(hours) || getIsDoubleTime(hours);

export const getWorkTimeType = (hours: number, totalMultiplier?: number) => {
  if (getIsTimeAndOneHalf(hours)) return WorkTimeType.TimeAndOneHalf;
  else if (getIsDoubleTime(hours))
    return totalMultiplier && totalMultiplier + 1 > 2
      ? WorkTimeType.TimeAndOneHalf
      : WorkTimeType.DoubleTime;
  else return WorkTimeType.StraightTime;
};

/**
 * Day of Week
 *
 * Note: moment's `.weekday()` is locale-aware; dayjs's `.day()` is not. Since neither
 * moment nor dayjs ever has a non-default locale set anywhere in this codebase, moment's
 * default `'en'` locale (week starts Sunday) makes `.weekday()` numerically identical to
 * `.day()` here (Sunday=0…Saturday=6), so this is a literal substitution, not an
 * approximation.
 */
export const getIsSaturday = (date: Dayjs | Date | string) =>
  (dayjs.isDayjs(date) ? date : dayjs(date)).day() === 6;
export const getIsSunday = (date: Dayjs | Date | string) =>
  (dayjs.isDayjs(date) ? date : dayjs(date)).day() === 0;
export const getIsWeekend = (date: Dayjs | Date | string) =>
  getIsSaturday(date) || getIsSunday(date);

/**
 * Time of Day
 */
export const getIsNight = (role: Role, date: Dayjs | Date | string) => {
  const currentHour = (dayjs.isDayjs(date) ? date : dayjs(date)).hour();
  return (
    (currentHour >= 20 && currentHour < 24) ||
    (currentHour >= 0 &&
      currentHour < (getIsExtra(role.performer_type) ? 1 : 6))
  );
};
export const getIsLateNight = (role: Role, date: Dayjs | Date | string) => {
  const currentHour = (dayjs.isDayjs(date) ? date : dayjs(date)).hour();
  return getIsExtra(role.performer_type)
    ? currentHour >= 1 && currentHour < 6
    : false;
};
export const getNightHours = (activity: SessionActivity) => {
  const nightStart = dayjs(activity.start_time)
    .hour(20)
    .minute(0)
    .second(0)
    .millisecond(0);
  return dayjs(activity.end_time).diff(nightStart) / MS_PER_HOUR;
};

/**
 * Holidays
 */
/** The holiday a date falls on (Talon's list, holidayDates), or undefined */
export const getHolidayName = (
  date: Dayjs | Date | string,
): string | undefined => {
  date = dayjs.isDayjs(date) ? date : dayjs(date);
  // holidayDates counts months from 1; dayjs from 0
  const month = date.month() + 1;
  const dayOfMonth = date.date();
  const weekday = date.day();

  return holidayDates[month]?.find((holiday) =>
    holiday.day
      ? holiday.day === dayOfMonth
      : !!holiday.days &&
        dayOfMonth >= holiday.days[0] &&
        dayOfMonth <= holiday.days[1] &&
        holiday.weekday === weekday,
  )?.name;
};

export const getIsHoliday = (date: Dayjs | Date | string): boolean =>
  !!getHolidayName(date);

/**
 * The premium day a date falls on: a holiday, or else a Saturday or Sunday. A holiday on a weekend is a holiday,
 * so work on it is paid one premium, not two.
 */
export const getPremiumDayType = (
  date: Dayjs | Date | string,
):
  | SessionDayType.Holiday
  | SessionDayType.Saturday
  | SessionDayType.Sunday
  | undefined => {
  date = dayjs.isDayjs(date) ? date : dayjs(date);
  if (getIsHoliday(date)) return SessionDayType.Holiday;
  if (date.day() === 6) return SessionDayType.Saturday;
  if (date.day() === 0) return SessionDayType.Sunday;
  return undefined;
};

/**
 * Estimate items: whether a value was edited by hand, i.e. differs from the contract's. Rates within half a
 * cent, and multipliers within a millionth, count as the same: the web sends back percentages it has
 * rounded (19.95% -> 0.19949999999999998).
 */
export const itemDefaultFields: TItemDefaultField[] = [
  'rate',
  'p_and_h_multiplier',
  'handling_multiplier',
  'taxes_multiplier',
];

export const isSameItemValue = (
  field: TItemDefaultField,
  a?: number | null,
  b?: number | null
) => Math.abs((a ?? 0) - (b ?? 0)) <= (field === 'rate' ? 0.005 : 1e-6);

export const isItemFieldEdited = (
  item: Partial<Record<TItemDefaultField, number | null>> & {
    defaults?: TItemDefaults | null;
  },
  field: TItemDefaultField
) => !!item.defaults && !isSameItemValue(field, item[field], item.defaults[field]);
